import * as React from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface PullToRefreshProps {
  onRefresh: () => Promise<unknown> | void;
  children: React.ReactNode;
  /** Disable on desktop (default true). */
  disabledOnDesktop?: boolean;
  className?: string;
}

const THRESHOLD = 70;
const MAX_PULL = 120;

/**
 * Native-feel pull-to-refresh wrapper. Activates only when the scroll
 * container is at the top and the user drags downward.
 */
export function PullToRefresh({
  onRefresh,
  children,
  disabledOnDesktop = true,
  className,
}: PullToRefreshProps) {
  const isMobile = useIsMobile();
  const enabled = isMobile || !disabledOnDesktop;
  const startY = React.useRef<number | null>(null);
  const [pull, setPull] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);

  const onTouchStart = (e: React.TouchEvent) => {
    if (!enabled || refreshing) return;
    if (window.scrollY > 0) return;
    startY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy <= 0) {
      setPull(0);
      return;
    }
    // dampened
    setPull(Math.min(MAX_PULL, dy * 0.5));
  };

  const onTouchEnd = async () => {
    if (startY.current == null) return;
    startY.current = null;
    if (pull >= THRESHOLD) {
      setRefreshing(true);
      try {
        if ((navigator as Navigator & { vibrate?: (n: number) => void }).vibrate) {
          (navigator as Navigator & { vibrate?: (n: number) => void }).vibrate?.(8);
        }
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
  };

  if (!enabled) {
    return <>{children}</>;
  }

  const offset = refreshing ? 48 : pull;

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className={cn("relative", className)}
    >
      <div
        aria-hidden={!refreshing && pull === 0}
        className="absolute inset-x-0 -top-12 flex items-center justify-center h-12 text-muted-foreground pointer-events-none"
        style={{
          transform: `translateY(${offset}px)`,
          transition: refreshing || startY.current == null ? "transform 200ms ease-out" : "none",
          opacity: Math.min(1, pull / THRESHOLD),
        }}
      >
        <RefreshCw
          className={cn("h-5 w-5", refreshing && "animate-spin")}
          style={{
            transform: refreshing ? undefined : `rotate(${(pull / THRESHOLD) * 360}deg)`,
          }}
        />
      </div>
      <div
        style={{
          transform: `translateY(${offset}px)`,
          transition: refreshing || startY.current == null ? "transform 200ms ease-out" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default PullToRefresh;
